import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetchMe } from '../../api/auth';

const STORAGE_KEY = 'jassp-auth-token';

const AuthContext = createContext(null);

function getStoredToken() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = getStoredToken();
    if (!token) {
      setReady(true);
      return;
    }
    apiFetchMe(token)
      .then((data) => {
        if (cancelled) return;
        setSession({ ...data.user, token });
      })
      .catch(() => {
        if (cancelled) return;
        try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback((payload = {}) => {
    const token = typeof payload.token === 'string' ? payload.token : '';
    const user = payload.user || {};
    const next = {
      email: typeof user.email === 'string' ? user.email : '',
      userId: typeof user.id === 'string' ? user.id : '',
      name: typeof user.name === 'string' ? user.name : '',
      role: typeof user.role === 'string' ? user.role : '',
      token,
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* ignore */
    }
    setSession(next);
  }, []);

  const logout = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setSession(null);
  }, []);

  const isAdmin = session?.role === 'admin';

  const value = useMemo(
    () => ({
      session,
      isAuthenticated: Boolean(session),
      isAdmin,
      login,
      logout,
      ready,
    }),
    [session, isAdmin, login, logout, ready],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      session: null,
      isAuthenticated: false,
      isAdmin: false,
      login: () => {},
      logout: () => {},
      ready: true,
    };
  }
  return ctx;
}
